import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card } from '@/shared/components/ui/card'
import { QueryErrorScreen } from '@/shared/components/ui/query-error-screen'
import { Pagination } from '@/shared/components/ui/pagination'
import { RefreshButton } from '@/shared/components/ui/refresh-button'
import { DEFAULT_PAGE_SIZE } from '@/shared/constants'
import { useMyBillings } from './hooks/useMyBillings'
import { usePayBilling } from './hooks/usePayBilling'
import { PayBillingDialog } from '@/features/payments/components/PayBillingDialog'
import { BillingFilters } from './components/BillingFilters'
import { BillingTable } from './components/BillingTable'
import { BillingListSkeleton } from './components/BillingSkeletons'
import { BILLING_STATUS_FILTERS } from './constants/billingStatusFilters'

const PAGE_SIZE = DEFAULT_PAGE_SIZE

export function BillingPage() {
  const [params, setParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const { pay, close, payBillingId } = usePayBilling()

  const statusValue = params.get('status') ?? 'all'
  const search = params.get('q') ?? ''
  const fromDate = params.get('from') ?? ''
  const toDate = params.get('to') ?? ''
  const statusFilter = BILLING_STATUS_FILTERS.find((f) => f.value === statusValue)?.statusFilter

  const { billings, totalItems, loading, error, refetch } = useMyBillings({
    take: PAGE_SIZE,
    skip: (page - 1) * PAGE_SIZE,
    statusFilter,
    search: search || undefined,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
  })

  const updateParam = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params)
    if (!value || value === 'all') next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
    setPage(1)
  }

  if (error && billings.length === 0) {
    return (
      <QueryErrorScreen
        error={error}
        onRetry={() => refetch()}
        title="No pudimos cargar tus facturas"
      />
    )
  }

  const isInitialLoading = loading

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-[36px] font-normal leading-[1.1] tracking-tight text-ink">
            Facturación
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            Consulta y gestiona las facturas de tus negocios.
          </p>
        </div>
        <RefreshButton onClick={async () => { await refetch() }} />
      </header>

      <Card>
        <div className="mb-4">
          <BillingFilters
            status={statusValue}
            onStatusChange={(v) => updateParam('status', v)}
            search={search}
            onSearchChange={(v) => updateParam('q', v)}
            fromDate={fromDate}
            onFromDateChange={(v) => updateParam('from', v)}
            toDate={toDate}
            onToDateChange={(v) => updateParam('to', v)}
          />
        </div>

        {isInitialLoading ? (
          <BillingListSkeleton />
        ) : billings.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-muted">
            No hay facturas con estos filtros.
          </p>
        ) : (
          <>
            <BillingTable
              billings={billings}
              onPay={pay}
            />
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              totalItems={totalItems}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <PayBillingDialog
        billingId={payBillingId}
        onClose={close}
        onPaid={() => {
          close()
          void refetch()
        }}
      />
    </div>
  )
}
